import React, { useState, useEffect, useRef } from "react";
import { Form, Button } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { AddPhotoAlternate, Close, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { addProductImages, getProductImages, deleteProductImages } from "../../../../../apis/Product/ProductImagesAPi";
import { getProductDetail, updateProduct } from "../../../../../apis/Product";
import EPDVerificationPanel from "./EPDVerificationPanel";
import DPPPanel from "./DPPPanel";
import DPPDocumentationPanel from "./DPPDocumentationPanel";
import "./ProductImageLabel.css";

const ProductImageLabel = ({
  productName,
  setProductName,
  productNumber,
  setProductNumber,
  productId,
  onEpdLockChange,
  lockStatus = "unlocked",
  lockRefreshTrigger = 0,
  dppData,
  onDppUpdate,
}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const businessId = queryParams.get("business") || localStorage.getItem("id_business");
  
  const [productImages, setProductImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [variationCategories, setVariationCategories] = useState([]);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  const currentProductDataRef = useRef(null); // Store current product data from backend
  const MAX_IMAGES = 10;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
  
  const CLOUD_NAME = "azilisdjo";
  const UPLOAD_PRESET = "upload";
  const UPLOAD_FOLDER = "productimages";

  // Load product details including variation categories and sync with parent state
  useEffect(() => {
    if (!productId || !businessId) return;

    getProductDetail({
      id: productId,
      business: businessId,
      setObjects: (product) => {
        // Store the full product object for reference when updating
        currentProductDataRef.current = product;
      },
      setName: (name) => {
        if (setProductName) setProductName(name || "");
      },
      setProductNum: (productNum) => {
        if (setProductNumber) setProductNumber(productNum || "");
      },
      setVariationCategories: (categories) => {
        setVariationCategories(categories || []);
        // Initialize selected variations state
        if (categories && categories.length > 0) {
          const initialSelections = {};
          categories.forEach(category => {
            initialSelections[category.id] = "";
          });
          setSelectedVariations(initialSelections);
        }
      },
    });
  }, [productId, businessId, setProductName, setProductNumber]);

  // Load images from backend on mount
  useEffect(() => {
    const loadImages = async () => {
      if (!productId) return;
      
      setIsLoading(true);
      try {
        const response = await getProductImages({ productId });
        if (response?.data?.images) {
          const formattedImages = response.data.images.map(img => ({
            url: img.image,
            publicId: img.public_id,
            objectId: img.id,
            assetId: img.id, // Using backend id as assetId
          }));
          setProductImages(formattedImages);
          if (formattedImages.length > 0) {
            setCurrentImageIndex(0);
          }
        }
      } catch (error) {
        console.error("Error loading images:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadImages();
  }, [productId]);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", UPLOAD_PRESET);
    formData.append("folder", UPLOAD_FOLDER);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error("Upload failed");
    }

    const data = await response.json();
    return {
      url: data.secure_url,
      publicId: data.public_id,
      assetId: data.asset_id,
    };
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files) return;

    const filesArray = Array.from(files);
    
    // Check total number of images
    if (productImages.length + filesArray.length > MAX_IMAGES) {
      alert(`Du kan maksimalt uploade ${MAX_IMAGES} billeder.`);
      e.target.value = ""; // Reset input
      return;
    }

    // Validate file sizes
    const invalidFiles = filesArray.filter(file => file.size > MAX_FILE_SIZE);
    if (invalidFiles.length > 0) {
      alert(`${invalidFiles.length} fil(er) er over 5MB grænsen.`);
      e.target.value = ""; // Reset input
      return;
    }

    setIsUploading(true);
    
    try {
      // Upload all files to Cloudinary
      const uploadPromises = filesArray.map(file => uploadToCloudinary(file));
      const uploadedImages = await Promise.all(uploadPromises);
      
      // Save to backend if productId exists
      if (productId) {
        try {
          const savePromises = uploadedImages.map(image => 
            addProductImages({
              productId: productId,
              image: image.url,
              publicId: image.publicId,
            })
          );
          await Promise.all(savePromises);
        } catch (backendError) {
          console.error("Error saving images to backend:", backendError);
          // Still add to state even if backend save fails
        }
      }
      
      // Add uploaded images to state
      setProductImages((prevImages) => {
        const newImages = [...prevImages, ...uploadedImages];
        if (prevImages.length === 0) {
          setCurrentImageIndex(0);
        }
        return newImages;
      });
    } catch (error) {
      console.error("Error uploading images:", error);
      alert("Fejl ved upload af billeder. Prøv igen.");
    } finally {
      setIsUploading(false);
      e.target.value = ""; // Reset input
    }
  };

  const removeCurrentImage = async () => {
    const imageToRemove = productImages[currentImageIndex];
    
    // Delete from backend if productId and objectId exist
    if (productId && imageToRemove?.objectId) {
      try {
        await deleteProductImages({
          productId: productId,
          objectId: imageToRemove.objectId,
          use_swal: false,
        });
      } catch (error) {
        console.error("Error deleting image from backend:", error);
        // Continue with local removal even if backend delete fails
      }
    }
    
    // Remove from state
    setProductImages(productImages.filter((_, i) => i !== currentImageIndex));
    if (currentImageIndex >= productImages.length - 1) {
      setCurrentImageIndex(Math.max(0, productImages.length - 2));
    }
  };

  const navigateImage = (direction) => {
    if (direction === 'prev') {
      setCurrentImageIndex((prev) => prev > 0 ? prev - 1 : productImages.length - 1);
    } else {
      setCurrentImageIndex((prev) => prev < productImages.length - 1 ? prev + 1 : 0);
    }
  };

  const getImageUrl = (image) => {
    // Handle both object format (from Cloudinary) and string format (backward compatibility)
    return typeof image === 'object' && image?.url ? image.url : image;
  };

  // Save product fields to backend with debouncing
  // Only sends the fields that are being updated, preserving existing values for others
  const saveProductField = (fieldName, value) => {
    if (!productId || !businessId) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for debounced save
    saveTimeoutRef.current = setTimeout(() => {
      setIsSaving(true);
      
      // Get current product data from backend (stored when loaded)
      const currentProduct = currentProductDataRef.current;
      
      // Build update payload - only include fields that are being updated
      const backendData = {
        id: productId,
        business: businessId,
      };
      
      // Always include name if it exists (required field)
      // Use updated value if provided, otherwise use current value from backend
      if (fieldName === 'name') {
        backendData.name = value;
      } else if (currentProduct?.name) {
        backendData.name = currentProduct.name;
      }
      
      // Only include the field being updated
      if (fieldName === 'productNum') {
        backendData.productNum = value;
      }

      updateProduct({
        ...backendData,
        setErrorMessage: (error) => {
          if (error) {
            console.error(`Error saving ${fieldName}:`, error);
          }
        },
        callback: () => {
          setIsSaving(false);
        },
      });
    }, 1000); // 1 second debounce
  };

  const handleNameChange = (e) => {
    const newValue = e.target.value;
    if (setProductName) setProductName(newValue);
    saveProductField('name', newValue);
  };

  const handleProductNumberChange = (e) => {
    const newValue = e.target.value;
    if (setProductNumber) setProductNumber(newValue);
    saveProductField('productNum', newValue);
  };

  const handleVariationChange = (categoryId, valueId) => {
    setSelectedVariations(prev => ({
      ...prev,
      [categoryId]: valueId,
    }));
    // TODO: Backend implementation needed
    // The backend needs to implement a way to save selected variation values.
    // Currently, variation_categories only stores the category structure (name, values),
    // but not the user's selected values. Backend should add a field like:
    // - selected_variation_values: JSONField containing [{ category_id, value_id }] array
    // OR
    // - variation_selections: JSONField containing { category_id: value_id } object mapping
    // Field should have null=True, blank=True to prevent errors when not filled
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="product-image-label-container">
      {/* Image Upload Section */}
      <div className="product-image-upload-box">
        {isLoading ? (
          <div className="product-image-upload-content">
            <span className="product-image-upload-text">Indlæser billeder...</span>
          </div>
        ) : productImages.length > 0 ? (
          <>
            <div className="product-image-overlay" />
            <img 
              src={getImageUrl(productImages[currentImageIndex])} 
              alt={`Produkt ${currentImageIndex + 1}`} 
              className="product-image-display"
            />
            
            {/* Navigation Arrows */}
            <Button
              variant="link"
              onClick={() => navigateImage('prev')}
              className="product-image-nav-btn product-image-nav-left"
              disabled={productImages.length <= 1}
            >
              <ChevronLeft />
            </Button>
            
            <Button
              variant="link"
              onClick={() => navigateImage('next')}
              className="product-image-nav-btn product-image-nav-right"
              disabled={productImages.length <= 1}
            >
              <ChevronRight />
            </Button>

            {/* Remove Image Button */}
            <Button
              variant="link"
              onClick={removeCurrentImage}
              className="product-image-remove-btn"
            >
              <Close fontSize="small" />
            </Button>

            {/* Image Counter */}
            <div className="product-image-counter">
              {currentImageIndex + 1} / {productImages.length}
            </div>
          </>
        ) : (
          <label className="product-image-upload-label" style={{ opacity: isUploading ? 0.5 : 1, pointerEvents: isUploading ? 'none' : 'auto' }}>
            <div className="product-image-upload-content">
              <div className="product-image-upload-icon">
                <AddPhotoAlternate fontSize="large" />
              </div>
              <span className="product-image-upload-text">
                {isUploading ? "Uploader..." : "Upload produktbillede"}
              </span>
              <span className="product-image-upload-subtext">Max 10 billeder, 5MB hver</span>
            </div>
            <input 
              type="file" 
              multiple 
              accept="image/*" 
              onChange={handleImageUpload}
              className="product-image-input-hidden"
              disabled={isUploading}
            />
          </label>
        )}
        
        {/* Add More Images Button */}
        {productImages.length > 0 && (
          <label 
            className="product-image-add-more-btn"
            style={{ opacity: isUploading ? 0.5 : 1, pointerEvents: isUploading ? 'none' : 'auto' }}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="product-image-input-hidden"
              disabled={isUploading}
            />
            <AddPhotoAlternate fontSize="small" />
            <span>{isUploading ? "Uploader..." : `+${MAX_IMAGES - productImages.length}`}</span>
          </label>
        )}
      </div>

      {/* Product Information */}
      <div className="product-info-box">
        <div className="product-info-content">
          <div className="product-info-field">
            <Form.Label className="product-info-label">Produktnavn</Form.Label>
            <Form.Control 
              value={productName || ""}
              onChange={handleNameChange}
              onBlur={(e) => {
                // Force save on blur if there's a pending timeout
                if (saveTimeoutRef.current) {
                  clearTimeout(saveTimeoutRef.current);
                  saveProductField('name', e.target.value);
                }
              }}
              placeholder="Indtast produktnavn"
              className="product-info-input"
              disabled={isSaving}
            />
            {isSaving && <small className="text-muted">Gemmer...</small>}
          </div>
          
          <div className="product-info-field">
            <Form.Label className="product-info-label">Produktnummer</Form.Label>
            <Form.Control 
              value={productNumber || ""}
              onChange={handleProductNumberChange}
              onBlur={(e) => {
                // Force save on blur if there's a pending timeout
                if (saveTimeoutRef.current) {
                  clearTimeout(saveTimeoutRef.current);
                  saveProductField('productNum', e.target.value);
                }
              }}
              placeholder="Indtast produktnummer"
              className="product-info-input"
              disabled={isSaving}
            />
            {isSaving && <small className="text-muted">Gemmer...</small>}
          </div>


          {/* FU variations */}
          {variationCategories && variationCategories.length > 0 && (
            <div className="product-info-fu-section">
              <Form.Label className="product-info-fu-title">FU variations</Form.Label>
              <div className="product-info-fu-fields">
                {variationCategories.map((category) => (
                  <div key={category.id} className="product-info-fu-field">
                    <Form.Label className="product-info-fu-label">{category.name}</Form.Label>
                    <Form.Select
                      className="product-info-select"
                      value={selectedVariations[category.id] || ""}
                      onChange={(e) => handleVariationChange(category.id, e.target.value)}
                    >
                      <option value="">Vælg {category.name.toLowerCase()}</option>
                      {category.values && category.values.map((value) => (
                        <option key={value.id} value={value.id}>
                          {value.value}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* EPD Verification Panel - below product info */}
      <div className="product-image-label-epd-panel mt-6">
        <EPDVerificationPanel
          productId={productId}
          businessId={businessId}
          onLockChange={onEpdLockChange}
          lcaLockStatus={lockStatus}
          lockRefreshTrigger={lockRefreshTrigger}
        />
      </div>

      {/* Digital Product Passport - visible after EPD lock */}
      <DPPPanel
        productId={productId}
        businessId={businessId}
        dppData={dppData}
        onDppUpdate={onDppUpdate}
        lcaLockStatus={lockStatus}
      />

      <DPPDocumentationPanel
        productId={productId}
        businessId={businessId}
        dppData={dppData}
        lcaLockStatus={lockStatus}
        refreshTrigger={lockRefreshTrigger}
      />
    </div>
  );
};

export default ProductImageLabel;
